import { cn } from '@/lib/utils';
import { formatEur, type CashFlowEntry, type MoneyMode } from '@/lib/cashFlow';
import type { LoanInstallmentRef } from '@/lib/cashFlowDetail';

/**
 * "Laenu osamakse" — the installment cross-reference panel on a materialized loan
 * entry (module 1.7). One loan payment splits into principal + interest (+ fee)
 * calendar entries; this block explains the split: it names THIS entry's part,
 * lists the sibling parts (clickable via the shared onOpenEntry drill, L14) and
 * shows the whole payment's total. EVERY figure is a stored/API-computed cent
 * value (totals summed by finance-core on the server, self read from the entry's
 * own money) — this component computes nothing (L9). The NET/GROSS toggle only
 * picks which stored figure is displayed.
 */

const PART_LABELS: Record<LoanInstallmentRef['part'], string> = {
  principal: 'Põhiosa',
  interest: 'Intress',
  fee: 'Lepingutasu',
};

export function LoanInstallmentSummary({
  entry,
  installment,
  mode,
  onOpenEntry,
}: {
  entry: CashFlowEntry;
  installment: LoanInstallmentRef | null;
  mode: MoneyMode;
  /** Open a sibling part's detail — the same shared drill navigation the
   *  related-documents panel uses (L14). Omitted → plain, non-clickable rows. */
  onOpenEntry?: (entryId: string) => void;
}) {
  if (installment === null) return null;
  const isNet = mode === 'net';
  const selfCents = isNet ? entry.money.net : entry.money.gross;
  const totalCents = isNet ? installment.totalNetCents : installment.totalGrossCents;

  return (
    <section className="rounded-lg border border-border bg-card/40" data-testid="loan-installment-summary">
      <header className="flex items-center gap-2 border-b border-border/70 px-3 py-2">
        <h3 className="text-[12px] font-semibold uppercase tracking-wide text-foreground">Laenu osamakse</h3>
        <span className="ml-auto text-[10px] text-muted-foreground/70">{`osamakse nr ${installment.seq}`}</span>
      </header>
      <ul className="flex flex-col gap-1 px-3 py-3">
        <li className="flex items-center gap-2 rounded border border-transparent px-2 py-1 text-[11px]">
          <span className="min-w-0 flex-1">
            <span className="flex items-center gap-1.5">
              <span className="truncate text-foreground">{PART_LABELS[installment.part]}</span>
              <span className="shrink-0 rounded border border-border px-1 text-[9px] uppercase tracking-wide text-muted-foreground/70">
                see kirje
              </span>
            </span>
          </span>
          <span className="shrink-0 text-right tabular-nums text-foreground/85">{formatEur(selfCents)}</span>
        </li>
        {installment.siblings.map((sibling) => {
          const label = PART_LABELS[sibling.part];
          const cents = isNet ? sibling.netCents : sibling.grossCents;
          return (
            <li
              key={sibling.entryId}
              className="flex items-center gap-2 rounded border border-transparent px-2 py-1 text-[11px] hover:border-border"
            >
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-1.5">
                  {onOpenEntry ? (
                    <button
                      type="button"
                      onClick={() => onOpenEntry(sibling.entryId)}
                      className="truncate text-foreground hover:text-[hsl(var(--primary))]"
                    >
                      {label}
                    </button>
                  ) : (
                    <span className="truncate text-foreground">{label}</span>
                  )}
                  {sibling.description && (
                    <span className="truncate text-muted-foreground">{sibling.description}</span>
                  )}
                </span>
              </span>
              <span className="shrink-0 text-right tabular-nums text-foreground/85">{formatEur(cents)}</span>
            </li>
          );
        })}
        <li
          className={cn(
            'mt-1 flex items-center gap-2 rounded border-t border-border/70 px-2 pb-1 pt-2 text-[11px]',
          )}
        >
          <span className="min-w-0 flex-1 font-semibold text-foreground">Osamakse kokku</span>
          <span className="shrink-0 text-right font-semibold tabular-nums text-foreground">
            {formatEur(totalCents)}
          </span>
        </li>
      </ul>
    </section>
  );
}
